import {useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../forecast-app/shared/hooks/useForecastFetchQuery';
import {parseReportTaxonomy, type ReportTaxonomy} from './contracts/report-taxonomy.types';
import type {ReportGrain} from './reportGrain';
import type {useReportTaxonomyQuery$data} from './__generated__/useReportTaxonomyQuery.graphql';

const reportTaxonomyQuery = graphql`
	query useReportTaxonomyQuery($reportType: String) {
		viewer {
			reportTaxonomy(reportType: $reportType)
		}
	}
`;

/** Resolves the taxonomy the conditions editor needs FOR THE GRAIN IT IS SHOWING.
 *
 * `grain` is the definition's, resolved by `grainOf`. A null grain means the definition names a
 * report this builder does not build: nothing is fetched and null is returned, which leaves the
 * conditions editor gated rather than offering it another grain's fields.
 *
 * `seed` is the taxonomy the chat surface may have passed through router state. It is honoured
 * only when it is already the right grain — then it is used as-is and no fetch is made, because
 * it was resolved moments ago in the same session and a refetch would replace it with an
 * identical one. A missing, wrong-grain or malformed seed fires the fetch instead, so the gap
 * that once left the "add condition" picker hidden whenever the surface was reached without a
 * resolved taxonomy stays closed. The saved-report path never has a seed at all.
 *
 * Returns null until a usable taxonomy is available; a malformed or empty-field-set response
 * leaves the current value unchanged so the editor stays gated rather than rendering an
 * unusable picker. */
export function useReportTaxonomy(grain: ReportGrain | null, seed?: ReportTaxonomy | null): ReportTaxonomy | null {
	// A seed is usable only when it is the grain being shown. Computed on every render but read
	// only by the mount effect and the initial state, both of which run once.
	// The same two conditions the fetched response is held to below: right grain, and a non-empty
	// field set. `parseReportTaxonomy` defaults `fields` to [], so a seed carrying only a reportType
	// parses to a truthy taxonomy with nothing in it -- which would satisfy the grain check, seed the
	// state, and suppress the fetch permanently, leaving the editor rendering the unusable picker
	// this hook exists to prevent.
	const usableSeed = grain !== null && seed != null && seed.reportType === grain && seed.fields.length > 0 ? seed : null;
	const [taxonomy, setTaxonomy] = useState<ReportTaxonomy | null>(usableSeed);
	const {data, fetch} = useForecastFetchQuery<useReportTaxonomyQuery$data, {reportType: string}>(reportTaxonomyQuery);

	// Fire at most once on mount. `fetch` is a fresh closure every render (the hook does not
	// memoise it), so a plain [fetch] dependency would refetch on every render — the ref guard
	// pins it to the first mount while keeping the dependency honest for the linter. Both
	// early returns sit INSIDE the guard, so a skipped fetch still burns the ref and cannot
	// fire on a later render.
	const fetchedRef = useRef(false);
	useEffect(() => {
		if (fetchedRef.current) {
			return;
		}
		fetchedRef.current = true;
		if (grain === null || usableSeed !== null) {
			return;
		}
		fetch({reportType: grain});
	}, [fetch, grain, usableSeed]);

	// The fetch hook signals completion via its returned `data`, not a callback argument, so react
	// to `data` here. Parse defensively: a malformed/empty JSON string must not crash the surface,
	// it should leave the taxonomy on its current value (seed or null).
	useEffect(() => {
		const raw = data?.viewer?.reportTaxonomy;
		if (!raw) {
			return;
		}
		try {
			const parsed = parseReportTaxonomy(raw);
			if (parsed.fields && parsed.fields.length > 0) {
				setTaxonomy(parsed);
			}
		} catch {
			// leave taxonomy as-is — condition editing stays gated
		}
	}, [data]);

	return taxonomy;
}
